/** Shared typed planning fields for creating, editing, planning and scheduling work orders. */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { EntityPicker, PartyPicker } from "@/shared/components/pickers";
import { Field, Input, Select } from "@/shared/ui/form";
import type { WorkOrderPlanningFields as PlanningValues } from "./queries";

type PlanningDefaults = Pick<
  Schemas["AssetOut"],
  "maintenance_team" | "operating_unit" | "cost_center"
>;

function toLocalDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** Render editable allocation and timing values, previewing inherited asset allocation. */
export function WorkOrderPlanningFields({
  values,
  onChange,
  defaults,
  prefix,
  scopeLocked = false,
}: {
  values: PlanningValues;
  onChange: <K extends keyof PlanningValues>(field: K, value: PlanningValues[K]) => void;
  defaults?: PlanningDefaults;
  prefix: string;
  scopeLocked?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const team = defaults?.maintenance_team ?? null;
  const unit = defaults?.operating_unit ?? null;
  const center = defaults?.cost_center ?? null;
  const valueFor = (value: string | null | undefined, inherited: string | undefined): string =>
    value === undefined ? (inherited ?? "") : (value ?? "");
  const selectedUnit =
    values.operating_unit_id === undefined ? (unit?.id ?? "") : (values.operating_unit_id ?? "");

  return (
    <div className="space-y-4">
      {defaults ? (
        <p className="text-sm text-muted-foreground">
          {t("workOrders.inheritedAllocation", {
            team: team?.name ?? "—",
            unit: unit?.name ?? "—",
            costCenter: center?.name ?? "—",
          })}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id={`${prefix}-team`} label={t("workOrders.team")}>
          <EntityPicker
            id={`${prefix}-team`}
            value={valueFor(values.assigned_team_id, team?.id)}
            onChange={(value) => onChange("assigned_team_id", value ?? null)}
            queryKey={["teams"]}
            load={async () =>
              unwrap(await api.GET("/api/v1/identity/teams")).map((row) => ({
                id: row.id,
                label: row.name,
              }))
            }
          />
        </Field>
        <Field id={`${prefix}-vendor`} label={t("workOrders.vendor")}>
          <PartyPicker
            id={`${prefix}-vendor`}
            value={values.vendor_id ?? ""}
            onChange={(value) => onChange("vendor_id", value ?? null)}
            role="vendor"
          />
        </Field>
        <Field id={`${prefix}-unit`} label={t("workOrders.operatingUnit")}>
          <PartyPicker
            id={`${prefix}-unit`}
            value={valueFor(values.operating_unit_id, unit?.id)}
            onChange={(value) => onChange("operating_unit_id", value ?? null)}
            role="operator"
          />
        </Field>
        <Field id={`${prefix}-center`} label={t("workOrders.costCenter")}>
          <EntityPicker
            id={`${prefix}-center`}
            value={valueFor(values.cost_center_id, center?.id)}
            onChange={(value) => onChange("cost_center_id", value ?? null)}
            queryKey={["cost-centers", selectedUnit]}
            load={async () => {
              if (!selectedUnit) return [];
              return unwrap(
                await api.GET("/api/v1/parties/cost-centers", {
                  params: { query: { operating_unit_id: selectedUnit } },
                }),
              ).items.map((row) => ({ id: row.id, label: `${row.code} · ${row.name}` }));
            }}
          />
        </Field>
        <Field id={`${prefix}-hours`} label={t("workOrders.estHours")}>
          <Input
            id={`${prefix}-hours`}
            type="number"
            min="0"
            step="0.25"
            value={values.est_hours ?? ""}
            onChange={(event) =>
              onChange("est_hours", event.target.value === "" ? null : event.target.value)
            }
          />
        </Field>
        <Field id={`${prefix}-due`} label={t("app.due")}>
          <Input
            id={`${prefix}-due`}
            type="datetime-local"
            value={toLocalDateTime(values.due_at)}
            onChange={(event) =>
              onChange(
                "due_at",
                event.target.value ? new Date(event.target.value).toISOString() : null,
              )
            }
          />
        </Field>
        <Field id={`${prefix}-scheduled-start`} label={t("workOrders.scheduledStart")}>
          <Input
            id={`${prefix}-scheduled-start`}
            type="datetime-local"
            value={toLocalDateTime(values.scheduled_start)}
            onChange={(event) =>
              onChange(
                "scheduled_start",
                event.target.value ? new Date(event.target.value).toISOString() : null,
              )
            }
          />
        </Field>
        <Field id={`${prefix}-scheduled-end`} label={t("workOrders.scheduledEnd")}>
          <Input
            id={`${prefix}-scheduled-end`}
            type="datetime-local"
            value={toLocalDateTime(values.scheduled_end)}
            onChange={(event) =>
              onChange(
                "scheduled_end",
                event.target.value ? new Date(event.target.value).toISOString() : null,
              )
            }
          />
        </Field>
        <Field id={`${prefix}-scope`} label={t("workOrders.restrictionScope")}>
          <Select
            id={`${prefix}-scope`}
            disabled={scopeLocked}
            value={values.restriction_scope ?? "component"}
            onChange={(event) => onChange("restriction_scope", event.target.value)}
          >
            {["component", "vessel", "both"].map((scope) => (
              <option key={scope} value={scope}>
                {t(`workOrders.restriction.${scope}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </div>
  );
}
